import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import { useMembershipApi } from '../api';
import { useData } from '../contexts/useData';
import { useAlert } from '@lendica/components';

export interface ResendDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    email: string;
    invitationId: string;
}

export default function ResendDialog({ open, setOpen, email, invitationId }: ResendDialogProps) {
    const [loading, setLoading] = useState(false);
    const { company, refreshInvitations } = useData();
    const { setSuccessMessage, setErrorMessage } = useAlert();
    const membershipApi = useMembershipApi();

    const handleClose = () => {
        setOpen(false);
    };

    const handleSendInvites = async () => {
        setLoading(true);
        if (!company?.id) return;
        try {
            const response = await membershipApi.resendInvitationById(invitationId);
            setSuccessMessage(response.message);
            setTimeout(() => {
                setLoading(false);
                handleClose();
                refreshInvitations();
            }, 2500);
        } catch (error: any) {
            setErrorMessage(error?.message || 'Failed to send invitations');
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                component: 'form',
                sx: {
                    paddingY: 1,
                    paddingX: 1,
                },
            }}
        >
            <DialogTitle>Resend invitation</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Resend invitation to the following email address.
                </DialogContentText>
                <Stack sx={{ my: 2, gap: 1 }}>
                    <TextField
                        label="Email"
                        fullWidth
                        value={email}
                        inputProps={{
                            readOnly: true,
                        }}
                    />
                    <TextField
                        label="Membership role"
                        fullWidth
                        value="Admin"
                        helperText="We only support admin role at the moment"
                        inputProps={{
                            readOnly: true,
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <LoadingButton
                    loading={loading}
                    onClick={handleSendInvites}
                    autoFocus
                    variant="contained"
                    color="primary"
                >
                    Resend invitation
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
